/**
 * Enquete de obrigações do cliente: perguntas simples ("tem funcionários?", "vende mercadoria?") em vez de marcar obrigação
 * por obrigação. O sistema cruza as respostas com o regime e o catálogo e propõe o que ligar e o que desligar.
 */
import { z } from 'zod';
import { CompetenciaChave, Id } from './comum.js';

export const PerfilRespostas = z.object({
  funcionarios: z.boolean().nullable(), proLabore: z.boolean().nullable(), comercio: z.boolean().nullable(), industria: z.boolean().nullable(), servicos: z.boolean().nullable(),
  imoveis: z.boolean().nullable(), saude: z.boolean().nullable(), retencoes: z.boolean().nullable(), bpo: z.boolean().nullable(), contabil: z.boolean().nullable(), certidoes: z.boolean().nullable(),
});
export type PerfilRespostas = z.infer<typeof PerfilRespostas>;
export type ChavePerfil = keyof PerfilRespostas;

export const PERGUNTAS_PERFIL: Array<{ chave: ChavePerfil; pergunta: string; ajuda: string }> = [
  { chave: 'funcionarios', pergunta: 'Tem funcionários registrados (CLT)?', ajuda: 'liga folha, eSocial, FGTS, 13º, férias e rescisões' },
  { chave: 'proLabore', pergunta: 'Paga pró-labore aos sócios?', ajuda: 'mesmo sem funcionários, o eSocial e a DCTFWeb ficam mensais' },
  { chave: 'comercio', pergunta: 'Vende mercadorias (comércio)?', ajuda: 'ICMS, GIA e EFD ICMS/IPI conforme o regime' },
  { chave: 'industria', pergunta: 'Fabrica produtos (indústria)?', ajuda: 'ICMS/IPI e as escriturações do estado' },
  { chave: 'servicos', pergunta: 'Presta serviços?', ajuda: 'ISS e a declaração de serviços do município' },
  { chave: 'imoveis', pergunta: 'Recebe ou intermedeia aluguéis / vende imóveis?', ajuda: 'DIMOB anual' },
  { chave: 'saude', pergunta: 'Atua na área da saúde (clínica, laboratório, plano)?', ajuda: 'DMED anual' },
  { chave: 'retencoes', pergunta: 'Retém tributos de terceiros ou contrata serviços com retenção?', ajuda: 'EFD-Reinf' },
  { chave: 'bpo', pergunta: 'O escritório faz o financeiro da empresa (BPO)?', ajuda: 'contas a pagar/receber, conciliações, cobrança, relatórios' },
  { chave: 'contabil', pergunta: 'O escritório faz a contabilidade completa (balanço, ECD/ECF)?', ajuda: 'fechamento contábil mensal e as escriturações anuais' },
  { chave: 'certidoes', pergunta: 'Acompanhar certidões e o certificado digital?', ajuda: 'CNDs periódicas e o vencimento do e-CNPJ' },
];

/** O que o sistema já sabe do cadastro (palpite para cada pergunta) e de onde tirou. */
export const PerfilCliente = z.object({ regime: z.string().nullable(), respostas: PerfilRespostas, pistas: z.array(z.string()) });
export type PerfilCliente = z.infer<typeof PerfilCliente>;
export const PerfilBody = z.object({ respostas: PerfilRespostas });
export type PerfilBody = z.infer<typeof PerfilBody>;

export const PerfilItem = z.object({
  obrigacaoId: Id, codigo: z.string(), sigla: z.string(), nome: z.string(), departamentoSugerido: z.string().nullable(), parametroChave: z.string().nullable(),
  /** por que entrou (ou não) no perfil */
  motivo: z.string(), ligada: z.boolean(), tarefasAbertas: z.number().int(),
});
export type PerfilItem = z.infer<typeof PerfilItem>;
export const PerfilResultado = z.object({
  /** no perfil e ainda não ligadas */ ligar: z.array(PerfilItem),
  /** no perfil e já ligadas */ manter: z.array(PerfilItem),
  /** ligadas, mas fora do perfil pelas respostas */ desligar: z.array(PerfilItem),
  /** fora do perfil e desligadas (ficam disponíveis para marcar à mão) */ foraDoPerfil: z.array(PerfilItem),
});
export type PerfilResultado = z.infer<typeof PerfilResultado>;
export const PerfilAplicarBody = z.object({
  ligar: z.array(Id).max(200), desligar: z.array(Id).max(200),
  competenciaInicio: CompetenciaChave.optional(), gerarRetroativoDesde: CompetenciaChave.nullable().optional(),
  parametros: z.record(Id, z.record(z.string(), z.union([z.string(), z.number()]))).optional(),
});
export type PerfilAplicarBody = z.infer<typeof PerfilAplicarBody>;
export const PerfilAplicado = z.object({ ligadas: z.number().int(), desligadas: z.number().int(), criadas: z.number().int(), avisos: z.array(z.string()) });
export type PerfilAplicado = z.infer<typeof PerfilAplicado>;
